import * as Types from '@aeppic/types'
import { SingleQueryStats } from 'model/indexing/query-stats.js'
import type {
  DocumentMatchIterator,
  DocumentMatchIteratorJoined,
  DocumentMatchIteratorUsingId,
  DocumentMatchIteratorUsingIndex,
  KMergedIntersectionIterator,
  KMergedUnionIterator
} from '../iterator-types.js'
import {
  KMergedIntersection,
  KMergedUnion,
  IterateAllDocuments,
  IterateNoDocuments,
  describeIterator,
  isIteratorOverNoDocuments,
  isIteratorUsingId,
  isIteratorUsingIndexOfDocuments,
  isIteratorUsingJoinsOfIterators
} from '../iterator-types.js'
import { legacyEnumerationStrategy } from './legacy.js'
import type { EnumerationStrategy } from './strategy.js'

class MinHeap<T> {
  private heap: T[] = []

  constructor(private readonly comparator: (a: T, b: T) => number) {}

  push(item: T): void {
    this.heap.push(item)
    this.siftUp()
  }

  pop(): T | undefined {
    if (this.heap.length === 0) return undefined
    const top = this.heap[0]
    const last = this.heap.pop()!
    if (this.heap.length > 0) {
      this.heap[0] = last
      this.siftDown()
    }
    return top
  }

  isEmpty(): boolean {
    return this.heap.length === 0
  }

  private siftUp(): void {
    let index = this.heap.length - 1
    while (index > 0) {
      const parentIndex = Math.floor((index - 1) / 2)
      if (this.comparator(this.heap[index], this.heap[parentIndex]) < 0) {
        ;[this.heap[index], this.heap[parentIndex]] = [this.heap[parentIndex], this.heap[index]]
        index = parentIndex
      } else {
        break
      }
    }
  }

  private siftDown(): void {
    let index = 0
    while (true) {
      const left = 2 * index + 1
      const right = 2 * index + 2
      let smallest = index

      if (left < this.heap.length && this.comparator(this.heap[left], this.heap[smallest]) < 0) {
        smallest = left
      }
      if (right < this.heap.length && this.comparator(this.heap[right], this.heap[smallest]) < 0) {
        smallest = right
      }

      if (smallest !== index) {
        ;[this.heap[index], this.heap[smallest]] = [this.heap[smallest], this.heap[index]]
        index = smallest
      } else {
        break
      }
    }
  }
}

class SeekableCursor {
  private iterator: Iterator<string>
  private currentValue: string | undefined

  constructor(private readonly source: DocumentMatchIteratorUsingIndex, startFrom?: string) {
    this.iterator = source.idIterator.keys(startFrom) as Iterator<string>
    this.currentValue = this.readNext()
  }

  get current(): string | undefined {
    return this.currentValue
  }

  get done(): boolean {
    return this.currentValue === undefined
  }

  advance(): void {
    this.currentValue = this.readNext()
  }

  seek(target: string): void {
    this.iterator = this.source.idIterator.keys(target) as Iterator<string>
    this.currentValue = this.readNext()
  }

  private readNext(): string | undefined {
    const next = this.iterator.next()
    return next.done ? undefined : (next.value as string)
  }
}

type Bounds = { lo?: string; hi?: string }

function computeBounds(iterators: DocumentMatchIteratorUsingIndex[]): Bounds {
  const bounds: Bounds = {}

  for (const it of iterators) {
    if (it.idIterator.size === 0) {
      return { lo: undefined, hi: undefined }
    }

    const lo = it.idIterator.minKey()
    const hi = it.idIterator.maxKey()

    if (lo === undefined || hi === undefined) {
      return { lo: undefined, hi: undefined }
    }

    if (bounds.lo === undefined || lo > bounds.lo) {
      bounds.lo = lo
    }

    if (bounds.hi === undefined || hi < bounds.hi) {
      bounds.hi = hi
    }
  }

  return bounds
}

function createSeekableCursors(
  iterators: DocumentMatchIteratorUsingIndex[],
  startFrom?: string
): SeekableCursor[] {
  return iterators.map(it => new SeekableCursor(it, startFrom))
}

function* intersectDocumentIds(
  iterators: DocumentMatchIteratorUsingIndex[],
  stats?: SingleQueryStats
): Generator<Types.DocumentId, void, unknown> {
  if (iterators.length < 2) {
    return
  }

  const { lo, hi } = computeBounds(iterators)

  if (lo && hi && lo > hi) {
    stats?.mark('btree-intersect:empty')
    return
  }

  const cursors = createSeekableCursors(iterators, lo)

  if (cursors.some(cursor => cursor.done)) {
    return
  }

  const telemetry = { k: cursors.length, loop: 0, globalLo: lo, globalHi: hi, skips: 0 }
  stats?.mark('btree-intersect:start', telemetry)

  const captureTiming = Boolean(stats)
  const intersectStart = captureTiming ? timeSource.now() : 0

  while (true) {
    telemetry.loop++

    let vmax = cursors[0].current as string
    for (let i = 1; i < cursors.length; i++) {
      const value = cursors[i].current as string
      if (value > vmax) {
        vmax = value
      }
    }

    const aligned = cursors.every(cursor => cursor.current === vmax)

    if (aligned) {
      stats?.increment('enumerated:intersection:btree')
      yield vmax

      for (const cursor of cursors) {
        cursor.advance()
        if (cursor.done) {
          return
        }
      }
    } else {
      for (const cursor of cursors) {
        if ((cursor.current as string) < vmax) {
          telemetry.skips++
          cursor.seek(vmax)
          if (cursor.done) {
            return
          }
        }
      }
    }
  }

  if (captureTiming) {
    stats?.mark('btree-intersect:timing', {
      durationMs: timeSource.now() - intersectStart,
      skipCount: telemetry.skips,
      loopCount: telemetry.loop
    })
  }
}

type UnionHeapEntry = {
  documentId: Types.DocumentId
  cursor: SeekableCursor
  origin: number
}

function* unionDocumentIds(
  iterators: DocumentMatchIteratorUsingIndex[],
  stats?: SingleQueryStats
): Generator<Types.DocumentId, void, unknown> {
  if (iterators.length === 0) {
    return
  }

  if (iterators.length === 1) {
    for (const docId of iterators[0].idIterator) {
      yield docId
    }
    return
  }

  if (USE_SIMPLE_UNION_STRATEGY) {
    const unionStart = stats ? timeSource.now() : 0
    const seen = new Set<Types.DocumentId>()

    for (const iterator of iterators) {
      for (const docId of iterator.idIterator) {
        seen.add(docId)
      }
    }

    const sortedIds = Array.from(seen).sort()

    stats?.mark('k-merge-union-stats', {
      heapOperations: 0,
      documentsYielded: sortedIds.length,
      opsPerDocument: 0,
      iteratorCount: iterators.length
    })

    if (stats) {
      stats.mark('k-merge-union:timing', {
        durationMs: timeSource.now() - unionStart,
        documentsYielded: sortedIds.length,
        iteratorCount: iterators.length,
        strategy: 'set-union-sort'
      })
    }

    for (const id of sortedIds) {
      yield id
    }

    return
  }

  const cursors = createSeekableCursors(iterators)
  const heap = new MinHeap<UnionHeapEntry>((a, b) => {
    if (a.documentId < b.documentId) return -1
    if (a.documentId > b.documentId) return 1
    return a.origin - b.origin
  })

  cursors.forEach((cursor, index) => {
    if (!cursor.done) {
      heap.push({ documentId: cursor.current as string, cursor, origin: index })
    }
  })

  let lastYielded: string | undefined
  let heapOperations = 0
  let documentsYielded = 0
  const captureTiming = Boolean(stats)
  const unionStart = captureTiming ? timeSource.now() : 0

  while (!heap.isEmpty()) {
    heapOperations++
    const entry = heap.pop()!

    if (entry.documentId !== lastYielded) {
      documentsYielded++
      yield entry.documentId
      lastYielded = entry.documentId
    }

    entry.cursor.advance()
    while (!entry.cursor.done && entry.cursor.current === lastYielded) {
      entry.cursor.advance()
    }

    if (!entry.cursor.done) {
      heapOperations++
      heap.push({
        documentId: entry.cursor.current as string,
        cursor: entry.cursor,
        origin: entry.origin
      })
    }
  }

  stats?.mark('k-merge-union-stats', {
    heapOperations,
    documentsYielded,
    opsPerDocument: documentsYielded > 0 ? heapOperations / documentsYielded : 0,
    iteratorCount: iterators.length
  })

  if (captureTiming) {
    stats?.mark('k-merge-union:timing', {
      durationMs: timeSource.now() - unionStart,
      documentsYielded,
      heapOperations
    })
  }
}

function* unionSortableIterators(
  iterators: DocumentMatchIterator[],
  allDocuments: Map<string, Types.Document> | undefined,
  stats?: SingleQueryStats
): Generator<Types.DocumentId, void, unknown> {
  const unionStart = stats ? timeSource.now() : 0
  const seen = new Set<Types.DocumentId>()

  for (const iterator of iterators) {
    for (const docId of getDocumentIdGeneratorCore(iterator, allDocuments, stats)) {
      seen.add(docId)
    }
  }

  const sortedIds = Array.from(seen).sort()

  stats?.mark('k-merge-union-stats', {
    heapOperations: 0,
    documentsYielded: sortedIds.length,
    opsPerDocument: 0,
    iteratorCount: iterators.length
  })

  if (stats) {
    stats.mark('k-merge-union:timing', {
      durationMs: timeSource.now() - unionStart,
      documentsYielded: sortedIds.length,
      iteratorCount: iterators.length,
      strategy: USE_SIMPLE_UNION_STRATEGY ? 'set-union-sort' : 'generator-union'
    })
  }

  for (const id of sortedIds) {
    yield id
  }
}

function asIndexIterators(iterators: DocumentMatchIterator[]): DocumentMatchIteratorUsingIndex[] | null {
  const result: DocumentMatchIteratorUsingIndex[] = []
  for (const iterator of iterators) {
    if (isIteratorUsingIndexOfDocuments(iterator)) {
      result.push(iterator as DocumentMatchIteratorUsingIndex)
    } else {
      return null
    }
  }
  return result
}

function* getDocumentIdGeneratorCore(
  iterator: DocumentMatchIterator,
  allDocuments?: Map<string, Types.Document>,
  stats?: SingleQueryStats
): Generator<Types.DocumentId, void, unknown> {
  if (isIteratorUsingIndexOfDocuments(iterator)) {
    for (const docId of iterator.idIterator) {
      yield docId
    }
    return
  }

  if (isIteratorUsingId(iterator)) {
    yield iterator.id
    return
  }

  if (iterator.type === IterateNoDocuments) {
    return
  }

  if (iterator.type === KMergedUnion) {
    yield* enumerateKMergedUnionCore(iterator as KMergedUnionIterator, allDocuments, stats)
    return
  }

  if (iterator.type === KMergedIntersection) {
    yield* enumerateKMergedIntersectionCore(iterator as KMergedIntersectionIterator, allDocuments, stats)
    return
  }

  if (iterator.type === IterateAllDocuments && allDocuments) {
    const sortedIds = [...allDocuments.keys()].sort()
    for (const id of sortedIds) {
      yield id
    }
    return
  }

  if (isIteratorUsingJoinsOfIterators(iterator)) {
    if (iterator.operator === 'union') {
      for (const subIterator of iterator.iterators) {
        yield* getDocumentIdGeneratorCore(subIterator, allDocuments, stats)
      }
      return
    }

    if (iterator.operator === 'intersect') {
      const subGenerators: Set<Types.DocumentId>[] = []
      for (const subIterator of iterator.iterators) {
        const docIds = new Set<Types.DocumentId>()
        for (const docId of getDocumentIdGeneratorCore(subIterator, allDocuments, stats)) {
          docIds.add(docId)
        }
        subGenerators.push(docIds)
      }

      if (subGenerators.length === 0) {
        return
      }

      let intersection = subGenerators[0]
      for (let i = 1; i < subGenerators.length; i++) {
        const next = new Set<Types.DocumentId>()
        for (const candidate of intersection) {
          if (subGenerators[i].has(candidate)) {
            next.add(candidate)
          }
        }
        intersection = next
      }

      const sorted = [...intersection].sort()
      for (const docId of sorted) {
        yield docId
      }
      return
    }

    throw new Error(`Unsupported operator for experimental iterator: ${iterator.operator}`)
  }

  yield* legacyEnumerationStrategy.getDocumentIdGenerator(iterator, allDocuments)
}

function* enumerateKMergedUnionCore(
  iterator: KMergedUnionIterator,
  allDocuments?: Map<string, Types.Document>,
  stats?: SingleQueryStats
): Generator<Types.DocumentId, void, unknown> {
  const indexIterators = asIndexIterators(iterator.iterators)

  if (indexIterators) {
    yield* unionDocumentIds(indexIterators, stats)
    return
  }

  const allSortable = iterator.iterators.every(it => (it as any).isSortable)

  if (allSortable) {
    yield* unionSortableIterators(iterator.iterators, allDocuments, stats)
    return
  }

  yield* legacyEnumerationStrategy.enumerateKMergedUnion(iterator, allDocuments, stats)
}

function* enumerateKMergedIntersectionCore(
  iterator: KMergedIntersectionIterator,
  _allDocuments?: Map<string, Types.Document>,
  stats?: SingleQueryStats
): Generator<Types.DocumentId, void, unknown> {
  const indexIterators = asIndexIterators(iterator.iterators)

  if (!indexIterators) {
    yield* legacyEnumerationStrategy.enumerateKMergedIntersection(iterator, _allDocuments, stats)
    return
  }

  yield* intersectDocumentIds(indexIterators, stats)
}

function iteratorContainsCore(
  iterator: DocumentMatchIteratorUsingIndex,
  documentId: Types.DocumentId
): boolean {
  return iterator.idIterator.has(documentId)
}

function* enumerateGraphIteratorCore(
  iterator: DocumentMatchIterator,
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  const documentsAlreadyReturned = new Set<string>()
  const info = { enumerated: 0, duplicates: 0 }
  stats?.phase('enumerate-graph', { iterator: describeIterator(iterator), info })
  const ops = stats?.newOpStats('enumerate-graph:yield')

  for (const document of enumerateGraphIteratorInternal(iterator, allDocuments, stats)) {
    info.enumerated++
    const id = document.id

    ops?.beginOp()
    if (!documentsAlreadyReturned.has(id)) {
      documentsAlreadyReturned.add(id)
      yield document
    } else {
      info.duplicates++
    }
    ops?.endOp()
  }
}

function* enumerateGraphIteratorInternal(
  iterator: DocumentMatchIterator,
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  if (isIteratorOverNoDocuments(iterator)) {
    return
  }

  if (isIteratorUsingId(iterator)) {
    const doc = allDocuments.get(iterator.id)
    if (doc) {
      yield doc
    }
    return
  }

  if (isIteratorUsingIndexOfDocuments(iterator)) {
    for (const docId of iterator.idIterator) {
      const doc = allDocuments.get(docId)
      if (doc) {
        yield doc
      }
    }
    return
  }

  if (iterator.type === KMergedUnion) {
    for (const docId of enumerateKMergedUnionCore(iterator as KMergedUnionIterator, allDocuments, stats)) {
      const doc = allDocuments.get(docId)
      if (doc) {
        stats?.increment('enumerated:union:k-merge')
        yield doc
      }
    }
    return
  }

  if (iterator.type === KMergedIntersection) {
    for (const docId of enumerateKMergedIntersectionCore(iterator as KMergedIntersectionIterator, allDocuments, stats)) {
      const doc = allDocuments.get(docId)
      if (doc) {
        stats?.increment('enumerated:intersection:k-merge')
        yield doc
      }
    }
    return
  }

  if (isIteratorUsingJoinsOfIterators(iterator)) {
    if (iterator.iterators.length === 1) {
      yield* enumerateGraphIteratorInternal(iterator.iterators[0], allDocuments, stats)
      return
    }

    if (iterator.operator === 'intersect' && iterator.isSortable) {
      const indexIterators = asIndexIterators(iterator.iterators)
      if (indexIterators) {
        for (const docId of intersectDocumentIds(indexIterators, stats)) {
          const doc = allDocuments.get(docId)
          if (doc) {
            stats?.increment('enumerated:intersection:sorted')
            yield doc
          }
        }
        return
      }
    }

    if (iterator.operator === 'union' && iterator.isSortable) {
      const indexIterators = asIndexIterators(iterator.iterators)
      if (indexIterators) {
        for (const docId of unionDocumentIds(indexIterators, stats)) {
          const doc = allDocuments.get(docId)
          if (doc) {
            stats?.increment('enumerated:union:sorted')
            yield doc
          }
        }
        return
      }
    }
  }

  yield* legacyEnumerationStrategy.enumerateGraphIterator(iterator, allDocuments, stats)
}

function* enumerateIntersectionOfIteratorsCore(
  iterators: DocumentMatchIterator[],
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  if (iterators.some(isIteratorOverNoDocuments)) {
    stats?.mark('intersection-iterator:none')
    return
  }

  const indexIterators = asIndexIterators(iterators)
  if (indexIterators) {
    yield* enumerateBTreeIntersectionCore(indexIterators, allDocuments, stats)
    return
  }

  yield* legacyEnumerationStrategy.enumerateIntersectionOfIterators(iterators, allDocuments, stats)
}

function* enumerateUnionOfIteratorsCore(
  iterators: (DocumentMatchIteratorJoined | DocumentMatchIteratorUsingIndex | DocumentMatchIteratorUsingId)[],
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  const indexIterators = asIndexIterators(iterators as DocumentMatchIterator[])
  if (indexIterators) {
    for (const docId of unionDocumentIds(indexIterators, stats)) {
      const doc = allDocuments.get(docId)
      if (doc) {
        stats?.increment('enumerated:union:set')
        yield doc
      }
    }
    return
  }

  yield* legacyEnumerationStrategy.enumerateUnionOfIterators(iterators, allDocuments, stats)
}

function* enumerateBTreeIntersectionCore(
  iterators: DocumentMatchIteratorUsingIndex[],
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  for (const docId of intersectDocumentIds(iterators, stats)) {
    const doc = allDocuments.get(docId)
    if (doc) {
      yield doc
    }
  }
}

export const experimentalEnumerationStrategy: EnumerationStrategy = {
  getDocumentIdGenerator: (iterator, allDocuments) =>
    getDocumentIdGeneratorCore(iterator, allDocuments),
  enumerateKMergedUnion: enumerateKMergedUnionCore,
  enumerateKMergedIntersection: enumerateKMergedIntersectionCore,
  enumerateAllDocumentsInIndexIterator: iterator =>
    legacyEnumerationStrategy.enumerateAllDocumentsInIndexIterator(iterator),
  iteratorContains: iteratorContainsCore,
  enumerateGraphIterator: enumerateGraphIteratorCore,
  enumerateIntersectionOfIterators: enumerateIntersectionOfIteratorsCore,
  enumerateUnionOfIterators: enumerateUnionOfIteratorsCore,
  _enumerateBTreeIntersection: enumerateBTreeIntersectionCore
}
const timeSource: { now: () => number } = typeof globalThis !== 'undefined' && globalThis.performance && typeof globalThis.performance.now === 'function'
  ? globalThis.performance
  : { now: () => Date.now() }

const USE_SIMPLE_UNION_STRATEGY = false
